import { useOutletContext } from "react-router-dom";
import { useState, useEffect, useRef } from 'react';
import { Accordion, AccordionItem, AccordionHeader, AccordionBody, FormGroup, Input, Label, Button, ButtonGroup, Modal, ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield, faGlobe, faExclamationCircle, faCircleCheck, faCircleExclamation, faGear } from '@fortawesome/free-solid-svg-icons';

import { translations } from './../../../../i18/translations.js';
import ConfirmDialog from './../../../../util/ConfirmDialog.js';
import './UserDataForm.css';

const UserDataForm = ({ selectedMember, onSave, onClose, fetchMemberData, adminMode }) => {

    const API_URL = process.env.REACT_APP_SERVICE_API_URL;

    const {lang, showAlert} = useOutletContext();
    const [accordionOpen, setAccordionOpen] = useState('0');
    const [userData, setUserData] = useState(selectedMember.baseData);
    const [selectedFee, setSelectedFee] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(userData.id === "-1" || !adminMode ? "1" : "2");
    const [selectedMembership, setSelectedMembership] = useState(null);

    const defaultTotal = 5000; //HUF

    useEffect(() => {
        setUserData(selectedMember.baseData);
    }, [selectedMember]);

    const toggleTabPanel = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const toggleConfirmDialog = () => setConfirmOpen(adminMode && !confirmOpen);

    const accordionToggle = (id) => {
        setAccordionOpen((prevOpen) => (prevOpen === id ? '' : id));
    };

    const handleUserDataInputChange = (e) => {
        const {id, value, type, checked} = e.target;

        setUserData((prevData) => ({
                ...prevData,
                [id]: type === 'checkbox' ? checked : value
            }));
    };

    const handleAffiliationInputChange = (e) => {
        if (!adminMode) {
            return;
        }
        const {id, value, type} = e.target;

        let newValue = value;

        if (id === "total" && type === "text") {
            newValue = value.replace(/\D/g, "");
        }

        setSelectedFee((prevData) => ({
                ...prevData,
                [id]: newValue
            }));
    };

    const showAffiliationDialog = (fee) => {
        if (fee.exemption === null) {
            fee.exemption = '0';
            fee.total = defaultTotal;
        }
        fee.isExemption = fee.exemption === "1";
        setSelectedFee(fee);
    };

    const closeAffiliationDialog = () => {
        setSelectedFee(null);
    };

    const saveAffiliation = async () => {
        if (!adminMode) {
            return;
        }
        try {
            const payload = {
                affiliation: {
                    userId: userData.id,
                    year: selectedFee.year,
                    pay: !selectedFee.isExemption,
                    value: selectedFee.total,
                    comment: selectedFee.comment
                }
            };
            const response = await fetch(`${API_URL}/admin/save_affiliation.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            showAlert(data.alert);
            closeAffiliationDialog();
            fetchMemberData(userData.id);
        } catch (error) {
            console.error("Error save affiliation:", error);
        }
    };

    const deleteAffiliation = async () => {
        if (!adminMode) {
            return;
        }
        toggleConfirmDialog();
        try {
            const payload = {
                affiliation: {
                    id: selectedFee.id
                }
            };
            const response = await fetch(`${API_URL}/admin/delete_affiliation.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            showAlert(data.alert);
            closeAffiliationDialog();
            fetchMemberData(userData.id);
        } catch (error) {
            console.error("Error save affiliation:", error);
        }
    };


    const showMembershipDialog = (membership) => {
        if (!adminMode) {
            return;
        }
        setSelectedMembership(membership);
    };

    const closeMembershipDialog = () => {
        if (!adminMode) {
            return;
        }
        setSelectedMembership(null);
    };

    const handleMembershipInputChange = (e) => {
        if (!adminMode) {
            return;
        }
        const {id, value, type, checked} = e.target;

        setSelectedMembership((prevData) => ({
                ...prevData,
                [id]: type === 'checkbox' ? checked : value
            }));
    };
    const saveUserData = (userData) => {
        if (!userData.fullname) {
            showAlert({type: "alert-danger", message: "error_validation_fullname"});
            setAccordionOpen("0");
        } else if (!userData.username) {
            showAlert({type: "alert-danger", message: "error_validation_usr"});
            setAccordionOpen("0");
        } else if (userData.username.length < 5) {
            showAlert({type: "alert-danger", message: "error_validation_usr_length"});
            setAccordionOpen("0");
        } else if (userData.changePwd && (!userData.pwd || userData.pwd.length < 5)) {
            showAlert({type: "alert-danger", message: "error_validation_pwd_length"});
            setAccordionOpen("0");
        } else if (!userData.email) {
            showAlert({type: "alert-danger", message: "error_validation_email"});
            setAccordionOpen("1");
        } else {
            onSave(userData);
        }
    };

    const saveMembership = async () => {
        if (!adminMode) {
            return;
        }
        try {
            const payload = {
                membership: {
                    id: selectedMembership.id,
                    userId: userData.id,
                    membershipStart: selectedMembership.membershipStart,
                    membershipEnd: selectedMembership.membershipEnd,
                    comment: selectedMembership.comment
                }
            };
            const response = await fetch(`${API_URL}/admin/save_membership.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            showAlert(data.alert);
            closeMembershipDialog();
            fetchMemberData(userData.id);
        } catch (error) {
            console.error("Error save membership:", error);
        }
    };

    const exitMembership = async () => {
        if (!adminMode) {
            return;
        }
        try {
            const payload = {
                membership: {
                    id: selectedMembership.id,
                    userId: userData.id,
                    membershipStart: selectedMembership.membershipStart,
                    membershipEnd: selectedMembership.membershipEnd
                }
            };
            const response = await fetch(`${API_URL}/admin/exit_membership.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            showAlert(data.alert);
            closeMembershipDialog();
            fetchMemberData(userData.id);
        } catch (error) {
            console.error("Error save membership:", error);
        }
    };

    return (
            <div className="mbkegy-content">
                {userData.id === "-1" ? (
                                    <FormGroup switch>
                                        <Input id="initMembership" type="switch" checked={userData.initMembership} onChange={handleUserDataInputChange} />
                                        <Label for="initMembership">{translations[lang].add_membership}</Label>
                                    </FormGroup>

                                    ) : (
                            <ButtonGroup className="d-flex w-100 mb-1">
                                <Button
                                    color="primary"
                                    outline
                                    onClick={() => toggleTabPanel("1")}
                                    active={activeTab === "1"}
                                    >
                                    {adminMode ? translations[lang].tab_user_data : translations[lang].tab_myuser_data}
                                </Button>
                                <Button
                                    color="primary"
                                    outline
                                    disabled={userData.id === "-1"}
                                    onClick={() => toggleTabPanel("2")}
                                    active={activeTab === "2"}
                                    >
                                    {adminMode ? translations[lang].tab_membership_data : translations[lang].tab_mymembership_data}
                                </Button>
                            </ButtonGroup>
                            )}
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                        <Accordion open={accordionOpen} toggle={accordionToggle}>
                            <AccordionItem>
                                <AccordionHeader targetId="0">
                                    <FontAwesomeIcon icon={faUserShield} className="me-3" />
                                    {translations[lang].personal_data}
                                </AccordionHeader>
                                <AccordionBody accordionId="0">
                                    <FormGroup floating>
                                        <Input id="nameprefix" type="text" value={userData.nameprefix} onChange={handleUserDataInputChange} maxLength={30}/>
                                        <Label for="nameprefix">{translations[lang].name_prefix_label}</Label>
                                    </FormGroup>
                                    <FormGroup floating>
                                        <Input id="fullname" type="text" value={userData.fullname} onChange={handleUserDataInputChange} maxLength={100}/>
                                        <Label for="fullname">{translations[lang].fullname_label}</Label>
                                    </FormGroup>
                                    <FormGroup floating>
                                        <Input id="username" type="text" value={userData.username} onChange={handleUserDataInputChange} autoComplete="username" maxLength={50}/>
                                        <Label for="username">{translations[lang].username_label}</Label>
                                    </FormGroup>
                                    {userData.id === "-1" ? null : (
                            <FormGroup switch>
                                <Input id="changePwd" type="switch" checked={userData.changePwd} onChange={handleUserDataInputChange}/>
                                <Label for="changePwd">{translations[lang].change_password}</Label>
                            </FormGroup>
                                                )}
                                    <FormGroup floating>
                                        <Input id="pwd" type="password" value={userData.pwd} onChange={handleUserDataInputChange} disabled={!userData.changePwd && userData.id !== "-1"}  maxLength={50}/>
                                        <Label for="pwd">{translations[lang].pwd_label}</Label>
                                    </FormGroup>
                                </AccordionBody>
                            </AccordionItem>
            
                            <AccordionItem>
                                <AccordionHeader targetId="1">
                                    <FontAwesomeIcon icon={faGlobe} className="me-3" />
                                    {translations[lang].workplace_data}
                                </AccordionHeader>
                                <AccordionBody accordionId="1">
                                    <FormGroup floating>
                                        <Input id="institute" type="text" value={userData.institute} onChange={handleUserDataInputChange}  maxLength={200}/>
                                        <Label for="institute">{translations[lang].workspace_label}</Label>
                                    </FormGroup>
                                    <FormGroup floating>
                                        <Input id="address" type="text" value={userData.address} onChange={handleUserDataInputChange} autoComplete="street-address"  maxLength={200}/>
                                        <Label for="address">{translations[lang].workspace_address_label}</Label>
                                    </FormGroup>
                                    <FormGroup floating>
                                        <Input id="post" type="text" value={userData.post} onChange={handleUserDataInputChange}  maxLength={200}/>
                                        <Label for="post">{translations[lang].workspace_post_label}</Label>
                                    </FormGroup>
                                    <FormGroup floating>
                                        <Input id="phone" type="text" value={userData.phone} onChange={handleUserDataInputChange} autoComplete="tel"  maxLength={200}/>
                                        <Label for="phone">{translations[lang].workspace_phone_label}</Label>
                                    </FormGroup>
                                    <FormGroup floating>
                                        <Input id="email" type="text" value={userData.email} onChange={handleUserDataInputChange} autoComplete="email"  maxLength={200}/>
                                        <Label for="email">{translations[lang].workspace_email_label}</Label>
                                    </FormGroup>
                                    <FormGroup switch>
                                        <Label for="statement">
                                            <FontAwesomeIcon icon={faExclamationCircle} />
                                            <strong>{translations[lang].user_statement_label}: </strong>{translations[lang].user_statement}
                                        </Label>
                                        <Input id="statement" type="switch" checked={userData.statement} onChange={handleUserDataInputChange} />
                                    </FormGroup>
                                </AccordionBody>
                            </AccordionItem>
                        </Accordion>
                        <Button color="primary" onClick={() => saveUserData(userData)}>
                            {translations[lang].save}
                        </Button>
                        <Button color="secondary" onClick={onClose}>
                            {translations[lang].cancel}
                        </Button>
                    </TabPane>
            
            
                    <TabPane tabId="2">
                        {!selectedMember.memberships?.length ? translations[lang].no_membership_data : ''}
                        {selectedMember && selectedMember.memberships && selectedMember.memberships.map((m, index) => (
                                <div key={index} className={`fieldset-container ${index > 0 ? 'expired' : ''}`}>
                                    <fieldset className={`fieldset-panel ${index > 0 ? 'expired' : ''}`}>
                                        <legend className={`fieldset-legend ${index > 0 ? 'expired' : ''}`}>
                                            {`${m.membershipStart} - ${m.membershipEnd === '' ? translations[lang].membership_stil : m.membershipEnd}`}
                                        </legend>
                                        <div className={`affiliations-container ${index > 0 ? 'expired' : ''}`}>
                                            {m.affiliationFees.length === 0 ? (
                                                        <div>No affiliation fees available</div>
                                                                    ) : (
                                                                    m.affiliationFees.map((f, jindex) => (
                                                                    <Button
                                                                        key={jindex}
                                                                        outline
                                                                        className={`af-btn ${f.id === -1 ? 'missing' : ''}`}
                                                                        color={`${f.id === -1 ? 'danger' : 'success'}`}
                                                                        onClick={() => showAffiliationDialog(f)}
                                                                        >
                                                                        {f.year}
                                                                        <FontAwesomeIcon 
                                                                            icon={f.id === -1 ? faCircleExclamation : faCircleCheck}
                                                                            className="me-3"
                                                                            style={{color: f.id === -1 ? 'darkred' : (f.exemption === '0' ? 'darkgreen' : 'orange')}}  />
                                                                    </Button>
                                                                                ))
                                                                    )}
                                        </div>
                                        <div className="d-flex justify-content-end">
                                            {adminMode && <Button outline size="sm" onClick={() => showMembershipDialog(m)}><FontAwesomeIcon icon={faGear} /></Button>}
                                        </div>
                                    </fieldset>
                                </div>
                                        ))}
                    </TabPane>
                </TabContent>
            
            
                {/* Modal for displaying affiliation fee details */}
                <Modal isOpen={!!selectedFee} toggle={closeAffiliationDialog}>
                    <ModalHeader toggle={closeAffiliationDialog}>{selectedFee && selectedFee.isExemption ? translations[lang].affiliation_exemption : translations[lang].affiliation_payment} - {selectedFee && selectedFee.year}</ModalHeader>
                    <ModalBody>
                        {selectedFee ? (
                                    selectedFee.id === -1 ? (
                                            adminMode ? (
                                                    /*Uj befizetes*/
                                            <>
                                            {selectedFee.exemption === "0" ? (
                                                                                        <FormGroup floating>
                                                                                            <Input id="total" type="text" value={selectedFee.total} onChange={handleAffiliationInputChange} maxLength={6} disabled={!adminMode}/>
                                                                                            <Label for="total">{translations[lang].affiliation_amount} [HUF]</Label>
                                                                                        </FormGroup>
                                                                            ) : (
                                                                                <FormGroup floating>
                                                                                    <Input id="comment" type="text" value={selectedFee.comment}  onChange={handleAffiliationInputChange} maxLength={100} disabled={!adminMode}/>
                                                                                    <Label for="comment">{translations[lang].affiliation_exemption}</Label>
                                                                                </FormGroup>
                                                                    )}
                                
                                            <FormGroup switch>
                                                <Label for="pay">
                                                    {translations[lang].affiliation_pay}
                                                </Label>
                                                <Input 
                                                    id="pay" 
                                                    type="switch" 
                                                    checked={!selectedFee.isExemption} 
                                                    disabled={!adminMode}
                                                    onChange={() => {
                                                                        setSelectedFee(prev => ({
                                                                                ...prev,
                                                                                isExemption: !prev.isExemption,
                                                                                exemption: prev.isExemption ? '0' : '1',
                                                                                total: prev.isExemption ? defaultTotal : null,
                                                                                comment: ''
                                                                            }))
                                                                    }                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                        } 
                                                    />
                                            </FormGroup>                       
                                            </>
                                                    ) : (
                                    <div className="bg-danger text-white p-3">{translations[lang].missing_payment}</div>
                                            )) : (
                                    /*befizetes details*/
                            <>
                            <FormGroup floating>
                                <Input id="cd" type="text" value={selectedFee.cd} disabled />
                                <Label for="cd">{translations[lang].affiliation_cd}</Label>
                            </FormGroup>                        
                            {selectedFee.exemption === "0" ? (
                                                <FormGroup floating>
                                                    <Input id="total" type="text" value={selectedFee.total} disabled />
                                                    <Label for="total">{translations[lang].affiliation_amount} [HUF]</Label>
                                                </FormGroup>
                                                    ) : (
                                                <FormGroup floating>
                                                    <Input id="comment" type="text" value={selectedFee.comment} disabled />
                                                    <Label for="comment">{translations[lang].affiliation_exemption}</Label>
                                                </FormGroup>
                                                    )}
                            </>
                                    )
                                    ) : null}
                    </ModalBody>
                    <ModalFooter>
                        {adminMode && selectedFee && selectedFee.id !== -1 && <Button color="danger" onClick={toggleConfirmDialog}>{translations[lang].delete}</Button>}
                        {adminMode && selectedFee && selectedFee.id === -1 && <Button color="success" onClick={saveAffiliation}>{translations[lang].save}</Button>}            
                    </ModalFooter>
                </Modal>
            
            
                <ConfirmDialog 
                    title={translations[lang].affiliation_delete}
                    yes={translations[lang].yes}
                    no={translations[lang].cancel}
                    isOpen={confirmOpen} 
                    toggle={toggleConfirmDialog} 
                    onConfirm={deleteAffiliation} 
                    message={translations[lang].affiliation_delete_confirm}
                    />
            
                {/* Modal for displaying membership details */}
                <Modal isOpen={!!selectedMembership && adminMode} toggle={closeMembershipDialog}>
                    <ModalHeader toggle={closeMembershipDialog}>{translations[lang].edit_membership}</ModalHeader>
                    <ModalBody>
                        {selectedMembership && adminMode ? (
                            <>
                            <FormGroup floating>
                                <Input id="membershipStart" type="date" value={selectedMembership.membershipStart} onChange={handleMembershipInputChange} />
                                <Label for="membershipStart">{translations[lang].membership_start}</Label>
                            </FormGroup>
                            <FormGroup floating>
                                <Input id="membershipEnd" type="date" value={selectedMembership.membershipEnd} onChange={handleMembershipInputChange} />
                                <Label for="membershipEnd">{translations[lang].membership_end}</Label>
                            </FormGroup>
                            <FormGroup floating>
                                <Input id="comment" type="text" value={selectedMembership.comment} onChange={handleMembershipInputChange} />
                                <Label for="comment">{translations[lang].membership_comment}</Label>
                            </FormGroup>            
                            </>
                                    ) : null}
                    </ModalBody>
                    <ModalFooter>
                        {adminMode && selectedMembership && <Button color="primary" onClick={saveMembership}>{translations[lang].save}</Button>}
                        {adminMode && selectedMembership && selectedMembership.membershipEnd === "" && <Button color="warning" onClick={exitMembership}>{translations[lang].membership_exit}</Button>}
                        {adminMode && selectedMembership && <Button color="secondary" onClick={closeMembershipDialog}>{translations[lang].cancel}</Button>}            
                    </ModalFooter>
                </Modal>
            
            </div>
            );
};

export default UserDataForm;
